import React from 'react';
import { motion } from 'framer-motion';
import { Point, FaceAnalysis } from './analyzeFace';
interface FaceHeatmapProps {
  imageSrc: string;
  landmarks: Point[];
  analysis: FaceAnalysis;
}
const flip = (p: Point) => ({
  x: 1 - p.x,
  y: p.y
});
const avg = (pts: Point[]): Point => ({
  x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
  y: pts.reduce((s, p) => s + p.y, 0) / pts.length
});
export function FaceHeatmap({
  imageSrc,
  landmarks,
  analysis
}: FaceHeatmapProps) {
  const hasLandmarks = landmarks.length === 68;
  type Zone = {
    x: number;
    y: number;
    rx: number;
    ry: number;
    color: string;
    label: string;
  };
  const zones: Zone[] = [];
  if (hasLandmarks) {
    // Cheeks
    const leftCheek = flip(avg([landmarks[2], landmarks[31], landmarks[41]]));
    const rightCheek = flip(avg([landmarks[14], landmarks[35], landmarks[46]]));
    const cheekColor = analysis.metrics.midFaceVolume === 'low' ? 'rgba(244, 114, 182, 0.45)' : 'rgba(34, 197, 94, 0.35)';
    zones.push({
      x: leftCheek.x,
      y: leftCheek.y,
      rx: 0.09,
      ry: 0.07,
      color: cheekColor,
      label: 'cheek'
    });
    zones.push({
      x: rightCheek.x,
      y: rightCheek.y,
      rx: 0.09,
      ry: 0.07,
      color: cheekColor,
      label: 'cheek'
    });
    // Lips
    const mouth = flip(avg(landmarks.slice(48, 68)));
    const mouthWidth = Math.abs(landmarks[54].x - landmarks[48].x);
    const lipColor = analysis.metrics.lipFullness === 'low' ? 'rgba(251, 146, 60, 0.45)' : 'rgba(34, 197, 94, 0.35)';
    zones.push({
      x: mouth.x,
      y: mouth.y,
      rx: mouthWidth * 0.7,
      ry: 0.035,
      color: lipColor,
      label: 'lips'
    });
    // Nasolabial
    const nlLeft = flip(avg([landmarks[31], landmarks[48]]));
    const nlRight = flip(avg([landmarks[35], landmarks[54]]));
    const nlColor = analysis.metrics.nasolabialProminence === 'high' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(251, 191, 36, 0.3)';
    zones.push({
      x: nlLeft.x,
      y: nlLeft.y,
      rx: 0.04,
      ry: 0.05,
      color: nlColor,
      label: 'nasolabial'
    });
    zones.push({
      x: nlRight.x,
      y: nlRight.y,
      rx: 0.04,
      ry: 0.05,
      color: nlColor,
      label: 'nasolabial'
    });
    // Chin / Jaw
    const chin = flip(landmarks[8]);
    const jawColor = analysis.metrics.jawDefinition === 'low' ? 'rgba(168, 85, 247, 0.4)' : 'rgba(34, 197, 94, 0.3)';
    zones.push({
      x: chin.x,
      y: chin.y - 0.02,
      rx: 0.08,
      ry: 0.04,
      color: jawColor,
      label: 'jaw'
    });
  }
  return <div className="relative w-full aspect-[3/4] sm:aspect-[4/5] rounded-2xl overflow-hidden bg-gray-100 shadow-lg" data-id="element-300">
      <img src={imageSrc} alt="Face Map" className="absolute inset-0 w-full h-full object-cover" style={{
      transform: 'scaleX(-1)'
    }} data-id="element-301" />

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-302">
        <defs data-id="element-303">
          <filter id="blur" x="-20%" y="-20%" width="140%" height="140%" data-id="element-304">
            <feGaussianBlur stdDeviation="1.5" data-id="element-305" />
          </filter>
        </defs>
        {zones.map((z, i) => <motion.ellipse key={i} cx={z.x * 100} cy={z.y * 100} rx={z.rx * 100} ry={z.ry * 100} fill={z.color} filter="url(#blur)" initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2 + i * 0.1
      }} style={{
        transformOrigin: `${z.x * 100}px ${z.y * 100}px`
      }} data-id="element-306" />)}
      </svg>

      <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full shadow-sm" data-id="element-307">
        Face Map
      </div>
    </div>;
}